import { createSignal, For, onMount, onCleanup, createMemo } from 'solid-js';
import { useVim } from '../vim/VimProvider';

interface Keystroke {
  id: number;
  key: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
}

/**
 * Keystroke Visualizer Component (Experimental)
 * Displays translucent key presses horizontally in bottom right
 * Adjusts position when vim hints are visible
 * Works across all themes
 */
export function KeystrokeVisualizer() {
  const vim = useVim();
  const [keystrokes, setKeystrokes] = createSignal<Keystroke[]>([]);
  let idCounter = 0;

  // Check if vim hints are visible (vim enabled, normal mode, help closed)
  const vimHintsVisible = createMemo(() => {
    const state = vim.state();
    return state.enabled && state.mode === 'normal' && !state.showHelp;
  });

  onMount(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't show modifier-only keys
      if (['Control', 'Alt', 'Shift', 'Meta'].includes(e.key)) {
        return;
      }

      const newKeystroke: Keystroke = {
        id: ++idCounter,
        key: e.key === ' ' ? 'Space' : e.key,
        ctrl: e.ctrlKey,
        alt: e.altKey,
        shift: e.shiftKey,
        meta: e.metaKey,
      };

      setKeystrokes(prev => [...prev.slice(-4), newKeystroke]);

      // Remove after animation
      setTimeout(() => {
        setKeystrokes(prev => prev.filter(k => k.id !== newKeystroke.id));
      }, 2000);
    };

    document.addEventListener('keydown', handleKeyDown, true);

    onCleanup(() => {
      document.removeEventListener('keydown', handleKeyDown, true);
    });
  });

  const formatKey = (k: Keystroke): string => {
    const parts: string[] = [];
    if (k.ctrl) {
      parts.push('Ctrl');
    }
    if (k.alt) {
      parts.push('Alt');
    }
    if (k.shift) {
      parts.push('Shift');
    }
    if (k.meta) {
      parts.push('Meta');
    }
    parts.push(k.key);
    return parts.join('+');
  };

  return (
    <div
      class="keystroke-visualizer"
      style={{
        position: 'fixed',
        bottom: vimHintsVisible() ? '4.5rem' : '2.5rem',
        right: '1rem',
        display: 'flex',
        'flex-direction': 'row',
        'align-items': 'center',
        gap: '0.5rem',
        'z-index': 9995,
        'pointer-events': 'none',
        'font-family': '"JetBrains Mono", "Fira Code", monospace',
        transition: 'bottom 0.2s ease',
      }}
    >
      <For each={keystrokes()}>
        {(keystroke, index) => (
          <div
            class="keystroke-item"
            style={{
              background: 'var(--card)',
              color: 'var(--fg)',
              border: '1px solid var(--border)',
              'border-radius': 'var(--radius, 0.5rem)',
              padding: '0.5rem 1rem',
              'font-size': '0.875rem',
              'font-weight': 500,
              'box-shadow': 'var(--shadow)',
              opacity: 1 - index() * 0.15,
              transform: `translateX(${-index() * 4}px) scale(${1 - index() * 0.05})`,
              transition: 'all 0.15s ease',
              'backdrop-filter': 'blur(8px)',
              '-webkit-backdrop-filter': 'blur(8px)',
              'white-space': 'nowrap',
            }}
          >
            {formatKey(keystroke)}
          </div>
        )}
      </For>
    </div>
  );
}
